/**
 * Notebook cell half of the pin payload sanitizer (F8/G9/G10) — split out of
 * `pinPayloadSanitizer.ts` to keep both files under ~200 lines. See that
 * file's header for the shared "tolerant-but-honest deserialization" intent.
 *
 * Unknown cell types are coerced to inert markdown — never executable code
 * (G10) — and invalid chart configs are stripped while the cell itself
 * survives.
 */

import type { PinnedNotebookPayload } from '../../../../types/electron';
import type { ChartConfig } from '../../../workspace/charts/types/chartTypes';
import { validateChartConfig } from '../../../workspace/charts/utils/chartConfigValidation';

/** Inline cells of a pinned notebook (the `source: 'cells'` variant). */
export type PinnedNotebookCells = Extract<PinnedNotebookPayload, { source: 'cells' }>['cells'];

type PinnedNotebookCell = PinnedNotebookCells[number];

const KNOWN_CELL_TYPES = new Set(['code', 'javascript', 'markdown']);

function isKnownCellType(value: unknown): value is PinnedNotebookCell['cellType'] {
  return typeof value === 'string' && KNOWN_CELL_TYPES.has(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Sanitizes pinned notebook cells read back from disk. Two distinct outcomes
 * are counted separately (K7) so callers can report them honestly:
 *  - `droppedCount` — the cell was REMOVED (a non-record entry).
 *  - `adjustedCount` — the number of CELLS that survived but were changed: an
 *    unrecognized `cellType` coerced to `'markdown'` (G10 — never `'code'`, so
 *    a corrupted/hand-edited cell can't resurrect as executable code), and/or
 *    an invalid `chartConfig` stripped. A cell contributes at most 1 (L5) —
 *    the toast reports "N notebook items were adjusted", so the unit is cells,
 *    not individual adjustments.
 */
export function sanitizeNotebookCells(
  cells: unknown[],
): { cells: PinnedNotebookCells; droppedCount: number; adjustedCount: number } {
  let droppedCount = 0;
  let adjustedCount = 0;
  const out: PinnedNotebookCell[] = [];

  for (const raw of cells) {
    if (!isRecord(raw)) {
      droppedCount++; // cell removed
      continue;
    }

    const cell: Record<string, unknown> = { ...raw };
    let cellAdjusted = false;

    if (!isKnownCellType(cell.cellType)) {
      cell.cellType = 'markdown';
      cellAdjusted = true; // cell survived, coerced
    }

    if (cell.chartConfig !== undefined) {
      // Guard before the validator: `validateChartConfig` assumes a
      // `ChartConfig`-shaped object and throws on the first property access
      // when handed `null` — a throw here would kill the whole sanitize call
      // over one corrupt cell, so non-record configs are stripped directly.
      const valid =
        isRecord(cell.chartConfig) && validateChartConfig(cell.chartConfig as ChartConfig).valid;
      if (!valid) {
        delete cell.chartConfig;
        cellAdjusted = true; // cell survived, chart stripped
      }
    }

    if (cellAdjusted) adjustedCount++;
    out.push(cell as PinnedNotebookCell);
  }

  return { cells: out, droppedCount, adjustedCount };
}
